import { useState, useCallback, useEffect, useMemo } from 'react';
import { uniqueId } from '@utils/uniqueId';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import { DraggableTabs } from '@components/shared/DraggableTabs/DraggableTabs';
import ProfileCard from '@features/tenant-profile/ProfileCard';
import type { TabItem } from '@components/shared/DraggableTabs/types';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import {
  getFormTemplateByScope,
  createFormTemplate,
  updateFormTemplateByScope,
} from '@/services/formTemplateService';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { getFormConfigs } from '@/services/formService';
import { getProductTypes } from '@/services/tenantService';
import { useCountries } from '@/hooks/useCountries';
import type { FormTemplateTab } from '@/types/formTemplate';
import { toDraggableTabItems, getRemovedTabDetails } from '@utils/tabHelpers';
import TabContentEditor from '@features/tenant-templates/TabContentEditor';
import type { TabWithClientIds } from '@features/tenant-templates/types';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import styles from './index.module.scss';

export const Route = createFileRoute('/w/tenant-templates/')({
  staticData: { breadcrumb: 'Client Onboarding Templates', breadcrumbKey: 'K_BREADCRUMB_TENANT_TEMPLATES' },
  component: TenantTemplatePage,
});

// ============================================
// HELPERS
// ============================================

/** Add client IDs to API data for DnD tracking + normalize nulls from API */
function toClientTabs(apiTabs: FormTemplateTab[]): TabWithClientIds[] {
  return apiTabs.map((tab) => ({
    ...tab,
    description: tab.description ?? '',
    _tabClientId: uniqueId('tab'),
    forms: (tab.forms ?? []).map((form) => ({
      ...form,
      filters: form.filters ?? {
        country: { matchAll: true, values: [] },
        productType: { matchAll: true, values: [] },
      },
      attributes: form.attributes ?? { isDefault: false },
      _clientId: uniqueId('form'),
    })),
  }));
}

/** Strip client IDs before sending to API */
function toApiTabs(clientTabs: TabWithClientIds[]): FormTemplateTab[] {
  return clientTabs.map((tab, i) => ({
    name: tab.name,
    description: tab.description,
    order: i + 1,
    forms: tab.forms.map((form, fi) => ({
      formConfigId: form.formConfigId,
      formName: form.formName,
      order: fi + 1,
      accessLevel: form.accessLevel,
      filters: form.filters,
      attributes: form.attributes,
    })),
  }));
}

function createEmptyTab(): TabWithClientIds {
  return {
    _tabClientId: uniqueId('tab'),
    name: '',
    description: '',
    order: 0,
    forms: [],
  };
}

// ============================================
// COMPONENT
// ============================================

function TenantTemplatePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const session = useAuthStore((s) => s.session);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const objectType = session?.organization.type ?? '';
  const objectId = session?.organization.id ?? '';
  const [auditOpen, setAuditOpen] = useState(false);

  const profileFields = useMemo(
    () => [
      { label: t('K_LABEL_ORGANISATION_NAME', 'Company Name'), value: '-' },
      { label: t('K_LABEL_SUBDOMAIN', 'Subdomain'), value: '-' },
      { label: t('K_LABEL_COUNTRY', 'Country'), value: '-' },
      { label: t('K_LABEL_MOBILE', 'Mobile'), value: '-' },
      { label: t('K_LABEL_PRODUCT_TYPES', 'Product Types'), value: '-' },
      { label: t('K_LABEL_FIRST_NAME', 'First Name'), value: '-' },
      { label: t('K_LABEL_LAST_NAME', 'Last Name'), value: '-' },
      { label: t('K_LABEL_EMAIL', 'Email'), value: '-' },
    ],
    [t],
  );

  // --- Reference data queries (lifted to page level) ---
  const { data: formConfigsData, refetch: refetchFormConfigs } = useQuery({
    queryKey: ['formConfigs', { size: 500 }],
    queryFn: () => getFormConfigs({ size: 500 }),
  });

  const { countries, isLoading: countriesLoading } = useCountries();

  const { data: productTypesData, isLoading: productTypesLoading } = useQuery({
    queryKey: ['productTypes'],
    queryFn: () => getProductTypes({ size: 100 }),
  });

  const formConfigs = formConfigsData?.content ?? [];
  const productTypes = productTypesData?.content ?? [];

  // --- Template query (single call by scope) ---
  const { data: template, isError: templateNotFound, error: templateErrorObj } = useQuery({
    queryKey: ['formTemplate', objectType, objectId, 'TENANT'],
    queryFn: () => getFormTemplateByScope(objectType, objectId, 'TENANT'),
    enabled: !!objectType && !!objectId,
    // No local retry override — the shared queryClient never retries
    // 4xxx/domain codes, so a "template not found" outcome won't retry.
  });

  // Distinguish 403 (no permission) from 404 (genuinely no template yet).
  // Without this, a forbidden response would silently fall through to the
  // "no template — create one" empty state, mis-cuing the user.
  const templateForbidden = isForbiddenError(templateErrorObj);
  const templateExists = !!template && !templateNotFound;

  // --- Local state ---
  const [tabs, setTabs] = useState<TabWithClientIds[]>([]);
  const [savedTabsJson, setSavedTabsJson] = useState('');
  const [activeTabId, setActiveTabId] = useState('');
  const [removedTabIds, setRemovedTabIds] = useState<Set<string>>(new Set());
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [tabNameErrors, setTabNameErrors] = useState<Map<string, string>>(new Map());
  const [formErrors, setFormErrors] = useState<Map<string, { country?: string; productType?: string }>>(new Map());

  // Sync from API on first load
  const [synced, setSynced] = useState(false);
  useEffect(() => {
    if (template && !synced) {
      setSynced(true);
      const clientTabs = toClientTabs(template.tabs);
      setTabs(clientTabs);
      setSavedTabsJson(JSON.stringify(toApiTabs(clientTabs)));
      if (clientTabs[0]) {
        setActiveTabId(clientTabs[0]._tabClientId);
      }
    }
  }, [template, synced]);

  const liveTabs = useMemo(
    () => tabs.filter((t) => !removedTabIds.has(t._tabClientId)),
    [tabs, removedTabIds],
  );

  const isDirty = useMemo(
    () => JSON.stringify(toApiTabs(liveTabs)) !== savedTabsJson,
    [liveTabs, savedTabsJson],
  );

  const removedTabDetails = useMemo(
    () => getRemovedTabDetails(tabs, removedTabIds),
    [tabs, removedTabIds],
  );

  const activeTabIndex = tabs.findIndex((t) => t._tabClientId === activeTabId);
  const activeTab = activeTabIndex >= 0 ? tabs[activeTabIndex] : undefined;
  const draggableItems = useMemo(() => toDraggableTabItems(tabs), [tabs]);

  // --- Mutations (onSuccess handled inline in performSave) ---
  const createMutation = useMutation({
    mutationFn: createFormTemplate,
  });

  const updateMutation = useMutation({
    mutationFn: (data: { scope: 'TENANT'; tabs: FormTemplateTab[] }) =>
      updateFormTemplateByScope(objectType, objectId, 'TENANT', data),
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // --- Handlers ---
  const handleTabClick = useCallback((tab: TabItem) => {
    setActiveTabId(tab.id);
  }, []);

  const handleReorder = useCallback((reorderedItems: TabItem[]) => {
    setTabs((prev) => {
      const tabMap = new Map(prev.map((t) => [t._tabClientId, t]));
      return reorderedItems
        .map((item) => tabMap.get(item.id))
        .filter((t): t is TabWithClientIds => t !== undefined);
    });
  }, []);

  const handleAddTab = useCallback(() => {
    const newTab = createEmptyTab();
    newTab.name = t('K_DEFAULT_TAB_NAME', 'New Tab {number}').replace('{number}', String(liveTabs.length + 1));
    setTabs((prev) => [...prev, newTab]);
    setActiveTabId(newTab._tabClientId);
  }, [liveTabs.length]);

  const handleTabChange = useCallback((tabIndex: number, updated: TabWithClientIds) => {
    setTabs((prev) => {
      const next = [...prev];
      next[tabIndex] = updated;
      return next;
    });
    // Clear tab name error when name becomes non-empty
    if (updated.name.trim()) {
      setTabNameErrors((prev) => {
        if (!prev.has(updated._tabClientId)) return prev;
        const next = new Map(prev);
        next.delete(updated._tabClientId);
        return next;
      });
    }
    // Clear form errors for forms that now have valid selections
    setFormErrors((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const form of updated.forms) {
        const existing = next.get(form._clientId);
        if (!existing) continue;
        const errs = { ...existing };
        if (errs.country && (form.filters.country.matchAll || form.filters.country.values.length > 0)) {
          delete errs.country;
        }
        if (errs.productType && (form.filters.productType.matchAll || form.filters.productType.values.length > 0)) {
          delete errs.productType;
        }
        if (!errs.country && !errs.productType) {
          next.delete(form._clientId);
          changed = true;
        } else if (errs.country !== existing.country || errs.productType !== existing.productType) {
          next.set(form._clientId, errs);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  const handleRemoveTab = useCallback((tab: TabItem) => {
    setRemovedTabIds((prev) => new Set(prev).add(tab.id));
    // If removing the active tab, fallback to previous non-removed tab
    if (tab.id === activeTabId) {
      const currentIndex = tabs.findIndex((t) => t._tabClientId === tab.id);
      const remaining = tabs.filter(
        (t, i) => !removedTabIds.has(t._tabClientId) && t._tabClientId !== tab.id && i < currentIndex,
      );
      const fallback = remaining[remaining.length - 1]
        ?? tabs.find((t) => !removedTabIds.has(t._tabClientId) && t._tabClientId !== tab.id);
      setActiveTabId(fallback?._tabClientId ?? '');
    }
  }, [activeTabId, tabs, removedTabIds]);

  const handleUndoRemoveTab = useCallback((tab: TabItem) => {
    setRemovedTabIds((prev) => {
      const next = new Set(prev);
      next.delete(tab.id);
      return next;
    });
  }, []);

  const performSave = useCallback(() => {
    const tabsToSave = tabs.filter((t) => !removedTabIds.has(t._tabClientId));
    const payload = { scope: 'TENANT' as const, tabs: toApiTabs(tabsToSave) };

    const onSuccess = () => {
      void queryClient.invalidateQueries({ queryKey: ['formTemplate', objectType, objectId, 'TENANT'] });
      setTabs(tabsToSave);
      setSavedTabsJson(JSON.stringify(toApiTabs(tabsToSave)));
      setRemovedTabIds(new Set());
      setShowSaveConfirm(false);
    };

    const onError = (error: unknown) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    };

    if (templateExists) {
      updateMutation.mutate(payload, { onSuccess, onError });
    } else {
      createMutation.mutate(payload, { onSuccess, onError });
    }
  }, [tabs, removedTabIds, templateExists, createMutation, updateMutation, queryClient, objectType, objectId, t]);

  const validateTabs = useCallback((): boolean => {
    const nameErrors = new Map<string, string>();
    const fErrors = new Map<string, { country?: string; productType?: string }>();
    let firstErrorTabId: string | undefined;

    for (const tab of liveTabs) {
      if (!tab.name.trim()) {
        nameErrors.set(tab._tabClientId, t('K_VALIDATION_TAB_NAME_REQUIRED', 'Tab name is required'));
        if (!firstErrorTabId) firstErrorTabId = tab._tabClientId;
      }
      for (const form of tab.forms) {
        const errs: { country?: string; productType?: string } = {};
        if (!form.filters.country.matchAll && form.filters.country.values.length === 0) {
          errs.country = t('K_VALIDATION_SELECT_COUNTRY_OR_TOGGLE', 'Select at least one country or toggle "Select all"');
        }
        if (!form.filters.productType.matchAll && form.filters.productType.values.length === 0) {
          errs.productType = t('K_VALIDATION_SELECT_PRODUCT_TYPE_OR_TOGGLE', 'Select at least one product type or toggle "Select all"');
        }
        if (errs.country || errs.productType) {
          fErrors.set(form._clientId, errs);
          if (!firstErrorTabId) firstErrorTabId = tab._tabClientId;
        }
      }
    }

    setTabNameErrors(nameErrors);
    setFormErrors(fErrors);

    if (nameErrors.size > 0 || fErrors.size > 0) {
      if (firstErrorTabId) setActiveTabId(firstErrorTabId);
      return false;
    }
    return true;
  }, [liveTabs, t]);

  const handleSaveClick = useCallback(() => {
    if (!validateTabs()) return;
    if (removedTabIds.size > 0) {
      setShowSaveConfirm(true);
    } else {
      performSave();
    }
  }, [removedTabIds.size, performSave, validateTabs]);

  const handleCancelSave = useCallback(() => {
    setShowSaveConfirm(false);
  }, []);

  // --- Forbidden (page-level — template is the entire reason this route exists) ---
  if (templateForbidden) {
    return (
      <div className="content-container">
        <Forbidden variant="page" hideAction />
      </div>
    );
  }

  return (
    <div className="content-container">
      {/* Profile Card */}
      <ProfileCard
        name={t('K_TENANT_DATA_MODAL', 'Client Data Modal')}
        fields={profileFields}
        avatarColor="red"
      />

      {/* Toolbar — Add Tab + Save + Audit */}
      {(hasPermission('TENANT_TEMPLATES_CREATE') || hasPermission('TENANT_TEMPLATES_UPDATE') || (hasPermission('AUDIT_READ') && template?.id)) && (
        <div className={styles.toolbar}>
          {(hasPermission('TENANT_TEMPLATES_CREATE') || hasPermission('TENANT_TEMPLATES_UPDATE')) && (
            <Button variant="outline" size="sm" onClick={handleAddTab}>
              {t('K_BUTTON_ADD_TAB', '+ Add Tab')}
            </Button>
          )}
          {isDirty && (
            <Button variant="filled" size="sm" onClick={handleSaveClick} disabled={isSaving}>
              {isSaving ? t('K_BUTTON_SAVING', 'Saving...') : t('K_SAVE', 'Save')}
            </Button>
          )}
          {hasPermission('AUDIT_READ') && template?.id && (
            <Button variant="outline" size="sm" onClick={() => setAuditOpen(true)}>
              {t('K_AUDIT_TITLE', 'Audit trail')}
            </Button>
          )}
        </div>
      )}

      {/* Tabs */}
      <DraggableTabs
        tabs={draggableItems}
        activeTabId={activeTabId}
        onTabClick={handleTabClick}
        onReorder={handleReorder}
        removable={hasPermission('TENANT_TEMPLATES_UPDATE') || hasPermission('TENANT_TEMPLATES_CREATE')}
        removedTabIds={removedTabIds}
        onRemove={handleRemoveTab}
        onUndoRemove={handleUndoRemoveTab}
      />

      {/* Tab content */}
      <div className={styles.tabContent}>
        {activeTab && !removedTabIds.has(activeTab._tabClientId) ? (
          <TabContentEditor
            tab={activeTab}
            tabIndex={activeTabIndex}
            onTabChange={handleTabChange}
            formConfigs={formConfigs}
            onFormSelectorFocus={refetchFormConfigs}
            countries={countries}
            productTypes={productTypes}
            countriesLoading={countriesLoading}
            productTypesLoading={productTypesLoading}
            tabNameError={tabNameErrors.get(activeTab._tabClientId)}
            formErrors={formErrors}
          />
        ) : (
          <p className={styles.tabContentPlaceholder}>
            {tabs.length === 0
              ? t('K_MESSAGE_ADD_TAB_GET_STARTED', 'Click "+ Add Tab" to get started.')
              : t('K_MESSAGE_SELECT_TAB_EDIT', 'Select a tab to edit its content.')}
          </p>
        )}
      </div>

      {/* Save confirmation modal — shown when there are removed tabs */}
      <Modal
        isOpen={showSaveConfirm}
        onClose={handleCancelSave}
        title={t('K_MODAL_CONFIRM_CHANGES', 'Confirm Changes')}
        size="sm"
        footer={
          <div className={styles.confirmFooter}>
            <Button variant="outline" size="sm" onClick={handleCancelSave}>
              {t('K_CANCEL', 'Cancel')}
            </Button>
            <Button variant="filled" size="sm" onClick={performSave} disabled={isSaving}>
              {isSaving ? t('K_BUTTON_SAVING', 'Saving...') : t('K_CONFIRM', 'Confirm')}
            </Button>
          </div>
        }
      >
        <div className={styles.confirmBody}>
          <p>{t('K_MESSAGE_TABS_PERMANENTLY_REMOVED', 'The following tabs will be permanently removed:')}</p>
          <ul className={styles.confirmList}>
            {removedTabDetails.map((tab, i) => (
              <li key={i}>
                {tab.name}{tab.formNames && ` - ${tab.formNames}`}
              </li>
            ))}
          </ul>
        </div>
      </Modal>

      <AuditTrailModal
        open={auditOpen && !!template?.id}
        onClose={() => setAuditOpen(false)}
        filter={{ resourceType: AUDIT_RESOURCE.CLIENT_TEMPLATE, resourceId: template?.id ?? '' }}
        subject={template?.name || undefined}
      />
    </div>
  );
}
